import { byRoomOrder } from '../utils/roomOrder';

/* ── Status maps ─────────────────────────────────────────
   API (ALL_CAPS) ↔ Frontend (Title Case)
──────────────────────────────────────────────────────── */
export const API_TO_FE_STATUS = {
  PENDING:             'Pending',
  CONFIRMED:           'Approved',
  PARTIALLY_CONFIRMED: 'Partially Confirmed',
  REJECTED:            'Rejected',
  CANCELLED:           'Cancelled',
  TRANSFERRED:         'Transferred',
  CHECKED_IN:          'Checked In',
  CHECKED_OUT:         'Checked Out',
  NO_SHOW:             'No Show',
};

export const FE_TO_API_STATUS = Object.fromEntries(
  Object.entries(API_TO_FE_STATUS).map(([k, v]) => [v, k]),
);

/* ── Guest House ─────────────────────────────────────── */
export function mapGuestHouse(gh) {
  return {
    id:           gh.id,
    name:         gh.name         || '',
    /* The real, persisted field — blank until an admin sets one. Room-label
       call sites fall back to derived initials themselves (see adminHelpers'
       roomLabel) rather than baking that guess in here, so editing a guest
       house never silently writes a guessed value back as the real short_name. */
    shortName:    gh.short_name || '',
    address:      gh.address      || '',
    contactEmail: gh.contact_email || '',
    contactPhone: gh.contact_phone || '',
    isActive:     gh.is_active !== false,
    description:  '',
    totalRooms:   0,
    geoLocationLink: gh.geo_location_link || '',
    autoBookEnabled:       gh.auto_book_enabled ?? false,
    autoBookMaxRooms:      gh.auto_book_max_rooms ?? 2,
    autoBookMinLeadDays:   gh.auto_book_min_lead_days ?? 3,
    autoBookLookaheadDays: gh.auto_book_lookahead_days ?? 10,
    autoBookMaxStayNights: gh.auto_book_max_stay_nights ?? null,
    roomTurnoverBufferMinutes: gh.room_turnover_buffer_minutes ?? 60,
    lateCheckoutGraceMinutes:  gh.late_checkout_grace_minutes ?? 30,
    autoReleaseMaintenance:    gh.auto_release_maintenance ?? false,
    allowRoomReuse:            gh.allow_room_reuse ?? false,
  };
}

/* ── Room ────────────────────────────────────────────── */
function deriveCapacity(typeName = '', fallback = 1) {
  const n = typeName.toLowerCase();
  if (n.includes('suite'))  return 3;
  if (n.includes('double')) return 2;
  return fallback;
}

export function mapRoom(apiRoom, ghList = [], typeCapacity = null) {
  const gh       = ghList.find(g => g.id === apiRoom.guest_house);
  const typeName = apiRoom.room_type_name || '';
  return {
    id:             apiRoom.id,
    guestHouseId:   apiRoom.guest_house,
    guestHouseName: gh?.name           || '',
    guestHouseShortName: gh?.shortName || '',
    roomType:       typeName,
    roomTypeId:     apiRoom.room_type,
    roomNumber:     apiRoom.room_number,
    floor:          apiRoom.floor      || '',
    pricePerNight:  parseFloat(apiRoom.base_price) || 0,
    status:             apiRoom.status === 'MAINTENANCE' ? 'maintenance' : 'available',
    maintenanceRemark:  apiRoom.maintenance_remark || '',
    maintenanceIsAuto:  apiRoom.maintenance_is_auto || false,
    maintenanceUntil:   apiRoom.maintenance_until || null,
    capacity:       typeCapacity ?? deriveCapacity(typeName),
    isAC:           typeName.toLowerCase().includes('ac') ||
                    typeName.toLowerCase().includes('suite'),
  };
}

const BOOKING_TYPE_LABELS = { OFFICIAL: 'Official', NON_OFFICIAL: 'Non-official', PERSONAL: 'Non-official' };

/* ── Booking ─────────────────────────────────────────── */
export function mapBooking(b, paymentResponsibilityOptions = []) {
  const status      = API_TO_FE_STATUS[b.status] || b.status;
  /* Sorted once here, so every screen reading a booking through this mapper
     gets the rooms in room order — the detail modal's room list, the dashboard
     chips, Front Desk, My Bookings. The API sorts them too; this keeps the
     order right whatever a caller hands us, and makes `firstAssign` below mean
     "the lowest room" rather than "whichever row came back first". */
  const assignments = [...(b.room_assignments || [])].sort(byRoomOrder);
  const firstAssign = assignments[0];
  const bookingType = BOOKING_TYPE_LABELS[b.booking_type] || b.booking_type || '';
  const payment     = paymentResponsibilityOptions.find(o => o.code === b.payment_responsibility)?.label
                        || b.payment_responsibility || '';

  return {
    /* identity */
    id:                    b.id,
    bookingCode:           b.booking_number || `BK-${String(b.id).padStart(4, '0')}`,

    /* applicant */
    employeeId:            b.employee_id             || '',
    applicantName:         b.applicant_name          || '',
    userName:              b.applicant_name          || '',
    applicantDesignation:  b.designation             || '',
    designation:           b.designation             || '',
    applicantDepartment:   b.department              || '',
    department:            b.department              || '',
    applicantMobileNumber: b.mobile                  || '',
    phone:                 b.mobile                  || '',
    emailId:               b.email                   || '',
    email:                 b.email                   || '',

    /* guest */
    guestName:             b.guest_name              || '',
    guestDesignation:      b.guest_designation       || '',
    guestMobileNumber:     b.guest_mobile            || '',
    guestAddress:          b.guest_address           || '',

    /* stay */
    checkIn:               b.arrival_date            || '',
    checkOut:              b.departure_date          || '',
    arrivalTime:           b.arrival_time            || '',
    departureTime:         b.departure_time          || '',
    numberOfDays:          b.num_days                || 0,
    roomCount:             String(b.rooms_required   || ''),
    guests:                b.rooms_required          || 0,

    /* booking meta */
    bookingType,
    visitPurpose:          b.purpose_of_visit        || '',
    purpose:               b.purpose_of_visit        || '',
    payment,
    paymentMode:           payment,
    /* Raw API values kept alongside the display labels — the edit form has to
       send back the option's code and match options to the booking's type. */
    paymentResponsibility: b.payment_responsibility || '',
    paymentResponsibilityOther: b.payment_responsibility_other || '',
    bookingTypeCode:       b.booking_type            || '',
    /* Advances taken (list endpoint, admin panel only); null when none. */
    advancePaid:           b.advance_paid ?? null,
    accompanyingPersonsCount: b.accompanying_persons_count || 0,
    officialAttachment:    b.official_attachment     || '',
    partialConfirmNote:    b.partial_confirm_note    || '',

    /* guest house preferences (sorted by priority) */
    guestHousePreferences: (b.preferences || [])
      .sort((a, c) => a.priority - c.priority)
      .map(p => p.guest_house_name || ''),
    /* same list, but keeping each preference's guest_house id — a GH_ADMIN's
       visible guestHouses list is server-scoped to only their own property,
       so this is what lets the Transfer dialog offer the applicant's other
       stated preferences even when they're guest houses this admin can't
       otherwise see. */
    guestHousePreferenceOptions: (b.preferences || [])
      .sort((a, c) => a.priority - c.priority)
      .map(p => ({
        id: p.guest_house,
        name: p.guest_house_name || '',
        roomRequests: (p.room_requests || []).map(rr => ({
          id:           rr.id,
          roomTypeId:   rr.room_type,
          roomTypeName: rr.room_type_name || '',
          roomTypeShortName: rr.room_type_short_name || '',
          capacity:     rr.capacity,
          count:        rr.count,
        })),
      }))
      .filter(p => p.id),

    /* room assignment — first room kept for backward compat, all rooms exposed */
    /* guestHouseId/Name = the house the indent belongs to (its queue, its
       admins, its billing). stayGuestHouse* = the house(s) actually holding a
       room, which is what a reader means by "which guest house" — the two
       differ whenever a room is given from somewhere other than the house the
       indent was filed with. Server falls the latter back to the former until
       a room is allotted. */
    guestHouseId:          b.assigned_gh               || null,
    guestHouseName:        b.assigned_gh_name          || '',
    guestHouseShortName:   b.assigned_gh_short_name    || '',
    stayGuestHouseIds:     b.stay_gh_ids               || [],
    stayGuestHouseNames:   b.stay_gh_names             || [],
    roomId:                firstAssign?.room            || null,
    roomNumber:            firstAssign?.room_number     || '',
    roomType:              firstAssign?.room_type       || firstAssign?.room_type_name || '',
    roomAssignments:       assignments.map(a => ({
      id:                    a.id,
      roomId:                a.room,
      roomNumber:            a.room_number         || '',
      roomType:              a.room_type            || a.room_type_name || '',
      guestHouseId:          a.guest_house          || null,
      guestHouseName:        a.guest_house_name     || '',
      guestHouseShortName:   a.guest_house_short_name || '',
      source:                a.source               || '',
      actualCheckin:         a.actual_checkin       || null,
      actualCheckout:        a.actual_checkout      || null,
      isNoShow:              a.is_no_show           || false,
      expectedCheckin:       a.expected_checkin     || null,
      expectedCheckout:      a.expected_checkout    || null,
      effectiveArrival:      a.effective_arrival    || '',
      effectiveArrivalTime:  a.effective_arrival_time || '',
      effectiveDeparture:    a.effective_departure  || '',
      effectiveDepartureTime: a.effective_departure_time || '',
      effectiveNumDays:      a.effective_num_days   || 0,
      hasCustomWindow:       a.has_custom_window    || false,
      guestName:             a.guest_name           || '',
      roomStatus:            a.room_status            || 'AVAILABLE',
      roomMaintenanceRemark: a.room_maintenance_remark || '',
      roomMaintenanceUntil:  a.room_maintenance_until  || null,
    })),

    /* extended detail (available on GET /bookings/{id}/) */
    transfers:             b.transfers      || [],
    subRequests:           b.sub_requests   || [],
    statusHistory:         b.status_history || [],
    remarks:               b.remarks        || [],

    /* The newest remark alone, which the list endpoint sends for every row so
       the dashboard's Remarks column has something to show — `remarks` above
       stays empty until the detail endpoint fills in the whole thread.

       The detail endpoint sends that thread but no `latest_remark`, and every
       admin action maps its detail response back over the row it came from
       (updateBookingLocal) — so without falling back to the head of the thread,
       approving a booking would blank its Remarks cell. Both are ordered
       newest-first by BookingRemark.Meta, so the head is the latest. */
    latestRemark:          b.latest_remark || (b.remarks || [])[0] || null,

    /* status */
    status,
    _apiStatus:            b.status,

    /* timestamps */
    createdAt:             b.created_at              || '',
    updatedAt:             b.updated_at              || '',

    /* read state — shared: the first admin to open an indent clears it for all */
    firstSeenAt:           b.first_seen_at           || null,
    isUnread:              !b.first_seen_at,
    /* Sent in by the external booking server (Namaste BHU) rather than raised
       by an admin from the panel. */
    isExternal:            Boolean(b.is_external),
    /* Moved between guest houses at least once. The list sends a flag; the
       detail sends the transfers themselves. */
    wasTransferred:        Boolean(b.was_transferred) || (b.transfers || []).length > 0,
  };
}

/* One row of the employee-history preview under the Employee ID field on
   Create Booking — narrower than mapBooking, which expects the full detail
   payload (room_assignments, preferences, ...) this endpoint doesn't send. */
export function mapEmployeeBookingHistoryRow(b) {
  return {
    id:                  b.id,
    bookingCode:         b.booking_number || `#${b.id}`,
    guestName:           b.guest_name || '',
    department:          b.department || '',
    bookingType:         b.booking_type === 'NON_OFFICIAL' ? 'Non-official' : 'Official',
    checkIn:             b.arrival_date || '',
    checkOut:            b.departure_date || '',
    numberOfDays:        b.num_days || null,
    roomsRequired:       b.rooms_required || null,
    status:              API_TO_FE_STATUS[b.status] || b.status,
    guestHouseName:      b.assigned_gh_name || '',
    guestHouseShortName: b.assigned_gh_short_name || '',
  };
}

/* ── Admin User ──────────────────────────────────────── */
export function mapAdminUser(u) {
  return {
    id:             u.id,
    username:       u.user?.username    || '',
    email:          u.user?.email       || '',
    firstName:      u.user?.first_name  || '',
    lastName:       u.user?.last_name   || '',
    name:           `${u.user?.first_name || ''} ${u.user?.last_name || ''}`.trim(),
    designation:    u.designation       || '',
    mobile:         u.mobile            || '',
    role:           u.role              || '',
    assignedGhs:    (u.assigned_guest_houses || []).map(g => ({
      id:   g.guest_house_id,
      name: g.guest_house__name || '',
    })),
  };
}

/* ── Sub-Request ─────────────────────────────────────── */
export function mapSubRequest(s) {
  return {
    id:                s.id,
    bookingId:         s.booking,
    requestedFromId:   s.requested_from,
    requestedFromName: s.requested_from_name  || '',
    raisedBy:          s.raised_by,
    roomTypeId:        s.room_type,
    roomTypeName:      s.room_type_name       || '',
    roomsNeeded:       s.rooms_needed         || 1,
    status:            s.status               || '',
    responseNote:      s.response_note        || '',
    respondedBy:       s.responded_by         || null,
    respondedAt:       s.responded_at         || null,
    createdAt:         s.created_at           || '',
  };
}

/* ── Mail ────────────────────────────────────────────── */
export function mapMailTemplate(t) {
  return {
    code:            t.code,
    label:           t.label,
    subject:         t.subject,
    requiresAmount:  Boolean(t.requires_amount),
  };
}

export function mapMailPreview(p) {
  return {
    subject: p.subject || '',
    body:    p.body    || '',
  };
}

export function mapSentEmail(e) {
  return {
    id:            e.id,
    template:      e.template,
    templateLabel: e.template_label || e.template,
    subject:       e.subject || '',
    body:          e.body    || '',
    empId:         e.emp_id  || '',
    sender:        e.sender  || '',
    amount:        e.amount  || null,
    trigger:       e.trigger || '',
    sentByName:    e.sent_by_name || '',
    sentAt:        e.sent_at || null,
  };
}

function toApiTime(t) {
  if (!t) return '';
  /* "HH:mm" → "HH:mm:00" */
  return /^\d{2}:\d{2}$/.test(t) ? `${t}:00` : t;
}

/* ── Booking form (frontend) → API payload ───────────── */
export function bookingToApi(data, guestHouseList = []) {
  const preferences = (data.guestHousePreferences || [])
    .filter(Boolean)
    .map((nameOrId, i) => {
      const id = typeof nameOrId === 'number'
        ? nameOrId
        : guestHouseList.find(g => g.name === nameOrId)?.id;
      if (!id) return null;

      const roomRequests = (data.roomRequestsByPreference?.[i] || [])
        .filter(r => r.roomTypeId && Number(r.count) >= 1)
        .map(r => ({ room_type: Number(r.roomTypeId), count: Number(r.count) }));

      return {
        guest_house: id,
        priority: i + 1,
        ...(roomRequests.length ? { room_requests: roomRequests } : {}),
      };
    })
    .filter(Boolean);

  /* fallback: use the primary guest house as preference 1 */
  if (!preferences.length && data.guestHouseId) {
    preferences.push({ guest_house: data.guestHouseId, priority: 1 });
  }

  const bookingType = data.bookingType === 'Other'    ? 'OFFICIAL'
                    : data.bookingType === 'Personal'  ? 'NON_OFFICIAL'
                    : (data.bookingType?.toUpperCase() || 'OFFICIAL');

  return {
    employee_id:            data.employeeId            || '',
    applicant_name:         data.applicantName         || data.userName        || '',
    designation:            data.applicantDesignation  || data.designation     || '',
    department:             data.applicantDepartment   || data.department      || '',
    email:                  data.emailId               || data.email           || '',
    mobile:                 data.applicantMobileNumber || data.phone           || '',
    guest_name:             data.guestName             || '',
    guest_designation:      data.guestDesignation      || '',
    guest_mobile:           data.guestMobileNumber     || '',
    guest_address:          data.guestAddress          || '',
    booking_type:           bookingType,
    purpose_of_visit:       data.visitPurpose          || data.purpose        || '',
    accompanying_persons_count: Number(data.accompanyingPersonsCount) || 0,
    arrival_date:           data.checkIn               || '',
    arrival_time:           toApiTime(data.arrivalTime)   || '12:00:00',
    departure_date:         data.checkOut              || '',
    departure_time:         toApiTime(data.departureTime) || '10:00:00',
    rooms_required:         Number(data.roomCount      || data.guests         || 1),
    payment_responsibility: data.paymentMode || data.payment || '',
    payment_responsibility_other: data.paymentModeOther || '',
    preferences,
  };
}

/* ── Billing settings & catalogue ──────────────────────
   Money and percentages stay the strings the API sends ("2.50", "19.05"):
   the server does every sum, and parsing them to floats here is how a bill
   would start disagreeing with itself by a paisa. */
export function mapBillingSettings(s) {
  return {
    guestHouseId:   s.guest_house,
    cgstPercent:    s.cgst_percent,
    sgstPercent:    s.sgst_percent,
    retentionDays:  s.retention_days,
    legalName:      s.legal_name || '',
    gstin:          s.gstin || '',
    billingAddress: s.billing_address || '',
    updatedAt:      s.updated_at || null,
  };
}

export function billingSettingsToApi(f) {
  return {
    cgst_percent:    String(f.cgstPercent).trim(),
    sgst_percent:    String(f.sgstPercent).trim(),
    retention_days:  Number(f.retentionDays),
    legal_name:      (f.legalName || '').trim(),
    gstin:           (f.gstin || '').trim().toUpperCase(),
    billing_address: (f.billingAddress || '').trim(),
  };
}

export function mapChargeCategory(c) {
  return { id: c.id, guestHouseId: c.guest_house, name: c.name || '', order: c.order ?? 0 };
}

export function mapChargeItem(i) {
  return {
    id:            i.id,
    guestHouseId:  i.guest_house,
    categoryId:    i.category,
    categoryName:  i.category_name || '',
    name:          i.name || '',
    unitLabel:     i.unit_label || '',
    unitPrice:     i.unit_price,
    gstApplicable: Boolean(i.gst_applicable),
    hsnSac:        i.hsn_sac || '',
    inDailyGrid:   Boolean(i.in_daily_grid),
    isActive:      Boolean(i.is_active),
    order:         i.order ?? 0,
  };
}

export function chargeItemToApi(f) {
  return {
    category:       Number(f.categoryId),
    name:           (f.name || '').trim(),
    unit_label:     (f.unitLabel || '').trim(),
    unit_price:     String(f.unitPrice).trim(),
    gst_applicable: Boolean(f.gstApplicable),
    hsn_sac:        (f.hsnSac || '').trim(),
    in_daily_grid:  Boolean(f.inDailyGrid),
    is_active:      Boolean(f.isActive),
    order:          Number(f.order) || 0,
  };
}

/* ── Bills & payments ─────────────────────────────────
   Read-only shapes: the server computes every amount, and a bill is replaced
   whole by each write's response. Money stays as exact strings. */
export function mapBillLine(l) {
  return {
    id:             l.id,
    kind:           l.kind,
    chargeItemId:   l.charge_item,
    assignmentId:   l.assignment,
    serviceDate:    l.service_date,
    description:    l.description,
    categoryName:   l.category_name,
    hsnSac:         l.hsn_sac || '',
    unitLabel:      l.unit_label || '',
    quantity:       l.quantity,
    unitPrice:      l.unit_price,
    gstApplicable:  Boolean(l.gst_applicable),
    rateOverridden: Boolean(l.rate_overridden),
    detail:         l.detail || {},
    cgstPercent:    l.cgst_percent,
    sgstPercent:    l.sgst_percent,
    taxable:        l.taxable,
    cgst:           l.cgst,
    sgst:           l.sgst,
    total:          l.total,
  };
}

export function mapPaymentSummary(s) {
  if (!s) return null;
  return {
    billTotal:  s.bill_total,
    received:   s.received,
    refunded:   s.refunded,
    netPaid:    s.net_paid,
    balanceDue: s.balance_due,
    refundDue:  s.refund_due,
    billFinal:  Boolean(s.bill_final),
    settled:    Boolean(s.settled),
  };
}

export function mapBill(b) {
  return {
    id:                    b.id,
    bookingId:             b.booking,
    billNumber:            b.bill_number,
    revision:              b.revision,
    status:                b.status,
    guestHouseId:          b.guest_house,
    isOfficial:            Boolean(b.is_official),
    cgstPercent:           b.cgst_percent,
    sgstPercent:           b.sgst_percent,
    graceMinutes:          b.grace_minutes,
    billedToName:          b.billed_to_name || '',
    billedToAddress:       b.billed_to_address || '',
    billedToGstin:         b.billed_to_gstin || '',
    notes:                 b.notes || '',
    taxableTotal:          b.taxable_total,
    cgstTotal:             b.cgst_total,
    sgstTotal:             b.sgst_total,
    total:                 b.total,
    roundOff:              b.round_off,
    grandTotal:            b.grand_total,
    version:               b.version,
    createdByName:         b.created_by_name || '',
    createdAt:             b.created_at,
    finalizedAt:           b.finalized_at,
    cancelledAt:           b.cancelled_at,
    cancelReason:          b.cancel_reason || '',
    lines:                 (b.lines || []).map(mapBillLine),
    categoryTotals:        b.category_totals || [],
    earlyCheckouts:        (b.early_checkouts || []).map((e) => ({
      assignmentId:    e.assignment_id,
      room:            e.room,
      checkedIn:       e.checked_in,
      checkedOut:      e.checked_out,
      bookedUntil:     e.booked_until,
      daysSaved:       e.days_saved,
      suggestedAmount: e.suggested_amount,
      decided:         Boolean(e.decided),
      applied:         e.applied,
      amount:          e.amount,
    })),
    isStale:               Boolean(b.is_stale),
    stayDates:             b.stay_dates || null,
    warnings:              b.warnings || [],
    paymentSummary:        mapPaymentSummary(b.payment_summary),
  };
}

export function mapPayment(p) {
  return {
    id:            p.id,
    kind:          p.kind,
    mode:          p.mode,
    amount:        p.amount,
    receiptNumber: p.receipt_number || '',
    reference:     p.reference || '',
    remarks:       p.remarks || '',
    receivedBy:    p.received_by_name || '',
    receivedAt:    p.received_at,
    isVoid:        Boolean(p.is_void),
    voidReason:    p.void_reason || '',
    voidedBy:      p.voided_by_name || '',
    voidedAt:      p.voided_at,
  };
}

/* GET …/bill/print/ — the bill, its payments, and what only the printout
   needs (header, booking particulars, rows folded for print, tax summary). */
export function mapBillPrint(d) {
  const p = d.print;
  return {
    bill:     mapBill(d.bill),
    payments: (d.payments || []).map(mapPayment),
    header:   { ...p.header },
    booking: {
      bookingNumber:         p.booking.booking_number,
      guestName:             p.booking.guest_name,
      guestDesignation:      p.booking.guest_designation,
      guestMobile:           p.booking.guest_mobile,
      applicantName:         p.booking.applicant_name,
      employeeId:            p.booking.employee_id,
      department:            p.booking.department,
      bookingType:           p.booking.booking_type,
      paymentResponsibility: p.booking.payment_responsibility,
      purposeOfVisit:        p.booking.purpose_of_visit,
    },
    stays: p.stays.map((s) => ({ rooms: s.rooms, checkedIn: s.checked_in, checkedOut: s.checked_out, days: s.days })),
    rows: p.rows.map((r) => ({
      kind: r.kind, description: r.description, categoryName: r.category_name, hsnSac: r.hsn_sac,
      unitLabel: r.unit_label, quantity: r.quantity, unitPrice: r.unit_price,
      cgstPercent: r.cgst_percent, sgstPercent: r.sgst_percent,
      taxable: r.taxable, cgst: r.cgst, sgst: r.sgst, total: r.total,
    })),
    taxSummary: p.tax_summary.map((t) => ({
      cgstPercent: t.cgst_percent, sgstPercent: t.sgst_percent, taxable: t.taxable, cgst: t.cgst, sgst: t.sgst,
    })),
    amountInWords: p.amount_in_words,
  };
}

/* GET /bookings/<id>/bills/ — every revision of a booking's bill. */
export function mapBillRevision(r) {
  return {
    id:           r.id,
    billNumber:   r.bill_number,
    revision:     r.revision,
    status:       r.status,
    grandTotal:   r.grand_total,
    createdAt:    r.created_at,
    finalizedAt:  r.finalized_at,
    cancelledAt:  r.cancelled_at,
    cancelReason: r.cancel_reason || '',
  };
}
